// A node's folder over the page (it replaced the old Files link and the separate Space app): its files,
// then its notes. No subfolders, no comments. Rights come from the server (FolderBundle); it checks again.
//   upload     "Upload files", or drop them anywhere on the folder; several at once, each with its progress
//   open       a file's name, its icon or Open: the preview (FilePreview.tsx), ← → through the list in its order
//   download   a plain link: the server answers 302 to a short-lived read address
//   delete     its uploader, an editor of the task, or an administrator (FolderFile.canDelete)
//   Open Topics  one Word or PDF file shown to anyone as the topic's full description (/apply/T-107): chosen and
//              taken off by an editor of the topic or an administrator (FolderBundle.canDescribe), offered while
//              the topic is open (SEEKING) or has one chosen; a badge on that file tells everyone else
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { descriptionKind, type FolderBundle, type FolderFile, type FolderSummary } from '@pathonline/shared';
import { useEffect, useId, useMemo, useRef, useState, type DragEvent } from 'react';
import { getJson, sendJson, useReadOnly, type Note } from '../api.ts';
import { FileBadge, FilePreview } from './FilePreview.tsx';
import { describeUrl, droppedFiles, fileMeta, fileUrl, fmtSize, folderLook, folderText, newestFirst, nodeLinks, type FileLinks } from './model.ts';
import { Notes } from './Notes.tsx';
import { folderKey, uploads, useUploads, type Upload, type Uploads } from './upload.ts';
import './folders.css';

const enc = encodeURIComponent;

/** What FolderModal asks; exported for the tests. */
export const folderQuery = (nodeId: string) => ({
  queryKey: folderKey(nodeId),
  queryFn: () => getJson<FolderBundle>(`/api/nodes/${enc(nodeId)}/folder`),
});

/** The old FOLDER path (DP:4667): the icon on a bar, a row, a grant card. */
export function FolderGlyph() {
  return (
    <svg viewBox="-5 -5 10 10" aria-hidden="true">
      <path d="M-4.5,-2.6 Q-4.5,-3.6 -3.5,-3.6 H-1.2 L0,-2.4 H3.5 Q4.5,-2.4 4.5,-1.4 V2.6 Q4.5,3.6 3.5,3.6 H-3.5 Q-4.5,3.6 -4.5,2.6 Z" />
    </svg>
  );
}

/**
 * The folder icon on a tree row or a grant card, and the folder it opens. Amber with files or notes,
 * faint and dashed when empty (only for someone who may add); nothing for someone who may not open it.
 */
export function FolderButton({ nodeId, label, summary }: { nodeId: string; label: string; summary?: FolderSummary }) {
  const readOnly = useReadOnly();
  const [open, setOpen] = useState(false);
  const look = folderLook(summary, readOnly);
  if (!look || !summary) return null;
  const what = folderText(summary);
  return (
    <>
      <button
        type="button"
        className={`fm-btn ${look}`}
        onClick={() => setOpen(true)}
        title={look === 'empty' ? 'Files — empty: click to add files or notes' : `Files — ${what}`}
        aria-label={`Files of ${label}: ${what}`}
      >
        <FolderGlyph />
        {summary.files > 0 && <span className="fm-btn-n">{summary.files}</span>}
      </button>
      {open && <FolderModal nodeId={nodeId} label={label} onClose={() => setOpen(false)} />}
    </>
  );
}

interface Props {
  nodeId: string;
  /** Shown until the folder has loaded. */
  label: string;
  onClose: () => void;
  /** The upload queue; the tests pass their own. */
  queue?: Uploads;
}

/** Focusable inside the box, for the Tab loop. */
const FOCUSABLE = 'button:enabled, a[href], [contenteditable="true"]';

export function FolderModal({ nodeId, label, onClose, queue = uploads }: Props) {
  const qc = useQueryClient();
  const q = useQuery(folderQuery(nodeId));
  const ups = useUploads(nodeId, queue);
  const box = useRef<HTMLDivElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const picker = useRef<HTMLInputElement>(null);
  /** The press started on the backdrop (a drag out of the box is not a click on it). */
  const down = useRef(false);
  const titleId = useId();
  const [editing, setEditing] = useState(false);
  // Files dragged over the folder: the drop hint shows. A count, as dragenter and dragleave come per child.
  const [over, setOver] = useState(0);
  const [say, setSay] = useState<Note | null>(null);
  const [deleting, setDeleting] = useState('');
  /** The file whose Open Topics choice is being saved. */
  const [describing, setDescribing] = useState('');
  /** The file in the preview, by id: an upload or a delete reorders the list under it. */
  const [viewing, setViewing] = useState<string | null>(null);
  const f = q.data;
  const can = !!f?.canWrite;
  const files = f ? newestFirst(f.files) : [];
  // Offered on an open topic, or one that has a file chosen (to take it off): not on every task's folder.
  const describes = !!f?.canDescribe && (f.seeking || !!f.descriptionFile);
  const links = useMemo(() => nodeLinks(nodeId), [nodeId]);
  // The preview's order is the list's. A file gone from the list (deleted elsewhere) closes it.
  const shown = viewing ? files.findIndex((x) => x.id === viewing) : -1;

  const add = (files: FileList | readonly File[] | null) => {
    if (!f || !can || !files?.length) return;
    setSay(null);
    queue.add(nodeId, Array.from(files), f.maxMb);
  };
  // Download is a plain link, the preview may have said "Unknown file": the list is read again (on Download, on
  // closing the preview), so a file deleted meanwhile leaves it.
  const reload = () => void qc.invalidateQueries({ queryKey: folderKey(nodeId) });
  // While the notes are edited: × asks first, Escape ends the editing, a click beside the folder does nothing.
  const close = () => {
    if (editing && !window.confirm('Close the folder without saving the notes?')) return;
    onClose();
  };

  // Focus moves into the folder, and back to its icon when it closes.
  useEffect(() => {
    const back = document.activeElement as HTMLElement | null;
    closeButton.current?.focus();
    return () => back?.focus?.();
  }, []);
  // The editor goes when the editing ends (Escape, Save): the focus stays in the folder.
  useEffect(() => {
    if (!editing && box.current && !box.current.contains(document.activeElement)) closeButton.current?.focus();
  }, [editing]);
  // A modal: Escape ends the notes editing first, then closes; Tab stays inside.
  // Not while the preview is open over it: it has its own keys, and its Escape closes only itself.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (shown >= 0) return;
      if (e.key === 'Escape' && !e.defaultPrevented) {
        e.preventDefault();
        if (editing) setEditing(false);
        else onClose();
        return;
      }
      if (e.key !== 'Tab' || !box.current) return;
      const all = [...box.current.querySelectorAll<HTMLElement>(FOCUSABLE)];
      const i = all.indexOf(document.activeElement as HTMLElement);
      // Wrap at either end; from outside the box (the page behind) come back in.
      const out = !box.current.contains(document.activeElement);
      if (e.shiftKey ? out || i === 0 : out || i === all.length - 1) {
        e.preventDefault();
        all[e.shiftKey ? all.length - 1 : 0]?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const remove = async (file: FolderFile) => {
    if (!window.confirm(`Delete “${file.name}”?`)) return;
    setDeleting(file.id);
    setSay(null);
    try {
      await sendJson('DELETE', fileUrl(nodeId, file.id));
      qc.setQueryData<FolderBundle>(folderKey(nodeId), (old) => old && { ...old, files: old.files.filter((x) => x.id !== file.id) });
      void qc.invalidateQueries({ queryKey: folderKey(nodeId) });
      void qc.invalidateQueries({ queryKey: ['views'] });
      setSay({ bad: false, text: `“${file.name}” deleted.` });
    } catch (e) {
      setSay({ bad: true, text: (e as Error).message });
    } finally {
      setDeleting('');
    }
  };

  /** Shows `file` to anyone as the topic's full description on Open Topics, or none (null). Asked first: it publishes. */
  const describe = async (file: FolderFile | null) => {
    if (!f) return;
    const current = f.files.find((x) => x.id === f.descriptionFile);
    if (file) {
      const replaces = current ? `\n\nIt replaces “${current.name}”.` : '';
      const when = f.seeking ? '' : ' once the topic is open again';
      if (!window.confirm(`Show “${file.name}” on Open Topics as the full description of this topic?\n\nAnyone on the internet can read and download it${when}.${replaces}`)) return;
    }
    setDescribing(file?.id ?? f.descriptionFile);
    setSay(null);
    try {
      await sendJson('PUT', describeUrl(nodeId), { fileId: file?.id ?? '' });
      qc.setQueryData<FolderBundle>(folderKey(nodeId), (old) => old && { ...old, descriptionFile: file?.id ?? '' });
      void qc.invalidateQueries({ queryKey: folderKey(nodeId) });
      void qc.invalidateQueries({ queryKey: ['topics'] });
      void qc.invalidateQueries({ queryKey: ['topicPage', nodeId] });
      setSay({ bad: false, text: file ? `“${file.name}” is now the full description on Open Topics.` : 'Open Topics no longer shows a full description for this topic.' });
    } catch (e) {
      setSay({ bad: true, text: (e as Error).message });
    } finally {
      setDescribing('');
    }
  };

  // Files from the computer only (not a dragged task or text). Every drag stops here: the Gantt or tree
  // row the folder was opened from must not take it for a task dropped on it.
  const hasFiles = (e: DragEvent) => e.dataTransfer.types.includes('Files');
  const drag = {
    onDragEnter: (e: DragEvent) => {
      e.stopPropagation();
      if (!hasFiles(e)) return;
      e.preventDefault();
      setOver((n) => n + 1);
    },
    onDragOver: (e: DragEvent) => {
      e.stopPropagation();
      if (!hasFiles(e)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = can ? 'copy' : 'none';
    },
    onDragLeave: (e: DragEvent) => {
      e.stopPropagation();
      if (hasFiles(e)) setOver((n) => Math.max(0, n - 1));
    },
    // A drop beside the box counts too: a file let go of anywhere must not open in the tab (and cut off uploads).
    onDrop: (e: DragEvent) => {
      e.stopPropagation();
      if (!hasFiles(e)) return;
      e.preventDefault();
      setOver(0);
      // A dropped folder is left out: the browser cannot send it.
      const { files, folders } = droppedFiles(e.dataTransfer);
      add(files);
      if (folders && can) setSay({ bad: true, text: 'Folders cannot be uploaded — drop the files inside them instead.' });
    },
  };

  return (
    <div
      className="modal-back fm-back"
      role="presentation"
      onMouseDown={(e) => (down.current = e.target === e.currentTarget)}
      onClick={(e) => {
        e.stopPropagation();
        if (down.current && e.target === e.currentTarget && !editing) onClose();
      }}
      {...drag}
    >
      <div ref={box} className={`modal fm ${over > 0 && can ? 'is-drop' : ''}`} role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <div className="fm-hd">
          <p className="fm-kicker">Folder · {nodeId}</p>
          <h2 id={titleId} title={f?.label ?? label}>
            Files · {f?.label ?? label}
          </h2>
          <button ref={closeButton} type="button" className="fm-x" onClick={close} aria-label="Close">
            ×
          </button>
        </div>
        <div className="fm-body">
          {q.isPending && <p className="fm-none">Loading…</p>}
          {q.isError && <p className="error">{q.error.message}</p>}
          {f && (
            <>
              <section className="fm-files" aria-label="Files">
                {can && (
                  <div className="fm-up-bar">
                    <button type="button" className="btn small" onClick={() => picker.current?.click()}>
                      ⬆ Upload files
                    </button>
                    <span className="fm-hint">or drop them here · up to {f.maxMb.toLocaleString('en-US')} MB each</span>
                    <input
                      ref={picker}
                      type="file"
                      multiple
                      hidden
                      onChange={(e) => {
                        add(e.target.files);
                        e.target.value = '';
                      }}
                    />
                  </div>
                )}
                {describes && f.seeking && !f.descriptionFile && (
                  <p className="fm-hint fm-pub-hint">
                    This topic is on Open Topics. Upload a Word or PDF file and click <b>Show on Open Topics</b> to give it a full description page.
                  </p>
                )}
                {say && (
                  <p className={`fm-msg ${say.bad ? 'error' : 'ok'}`} role="status">
                    {say.text}
                  </p>
                )}
                {ups.length > 0 && (
                  <ul className="fm-list" aria-label="Uploads">
                    {ups.map((u) => (
                      <UploadRow key={u.id} u={u} queue={queue} />
                    ))}
                  </ul>
                )}
                {files.length === 0 && ups.length === 0 && <p className="fm-none">No files yet.</p>}
                {files.length > 0 && (
                  <ul className="fm-list">
                    {files.map((x) => (
                      <FileRow
                        key={x.id}
                        links={links}
                        file={x}
                        busy={deleting === x.id || describing === x.id}
                        onOpen={() => setViewing(x.id)}
                        onDelete={() => void remove(x)}
                        onFollow={reload}
                        described={f.descriptionFile === x.id}
                        seeking={f.seeking}
                        onDescribe={describes && descriptionKind(x) ? (show) => void describe(show ? x : null) : undefined}
                      />
                    ))}
                  </ul>
                )}
              </section>
              <Notes nodeId={nodeId} notes={f.notes} canEdit={can} editing={editing} setEditing={setEditing} />
            </>
          )}
        </div>
        {over > 0 && can && (
          <div className="fm-drop" aria-hidden="true">
            Drop the files to upload them
          </div>
        )}
      </div>
      {/* Inside the backdrop for React (the window itself is put in <body>): its clicks stop here too, never reaching the row the folder was opened from. */}
      {shown >= 0 && (
        <FilePreview
          nodeId={nodeId}
          files={files}
          index={shown}
          onIndex={(i) => setViewing(files[i]!.id)}
          onClose={() => {
            setViewing(null);
            reload();
          }}
        />
      )}
    </div>
  );
}

/**
 * One file: its badge (an image: its small picture), name, size, who and when; Open (the preview), Download, Delete.
 * A click on the name or the badge opens the preview too. `onFollow`: Download clicked. No `onDelete` (a shared
 * link's page): no Delete. `links`: where Download points (nodeLinks, sharedLinks).
 * `described`: the topic's full description on Open Topics (a badge; `seeking`: shown now, else once it opens).
 * `onDescribe`: may show it there, or take it off (FolderModal only).
 */
export function FileRow({
  links,
  file,
  busy,
  onOpen,
  onDelete,
  onFollow,
  described = false,
  seeking = false,
  onDescribe,
}: {
  links: FileLinks;
  file: FolderFile;
  busy: boolean;
  onOpen: () => void;
  onDelete?: () => void;
  onFollow?: () => void;
  described?: boolean;
  seeking?: boolean;
  onDescribe?: (show: boolean) => void;
}) {
  return (
    <li className={`fm-file ${described ? 'is-pub' : ''}`}>
      {/* The mouse's way in; from the keyboard: the name or Open. */}
      <FileBadge name={file.name} thumb={file.thumb} onClick={onOpen} />
      <span className="fm-main">
        <button type="button" className="fm-name" title={file.name} onClick={onOpen}>
          {file.name}
        </button>
        <span className="fm-meta">
          {fileMeta(file)}
          {described && (
            <span
              className="fm-pub"
              title={seeking ? 'Anyone can read it as this topic’s full description on Open Topics.' : 'Shown on Open Topics once this topic is open to applicants again.'}
            >
              {seeking ? 'On Open Topics' : 'Chosen for Open Topics'}
            </span>
          )}
        </span>
      </span>
      <span className="fm-acts">
        {onDescribe &&
          (described ? (
            <button type="button" className="btn small ghost" disabled={busy} onClick={() => onDescribe(false)} aria-label={`Take ${file.name} off Open Topics`}>
              Take off Open Topics
            </button>
          ) : (
            <button type="button" className="btn small ghost" disabled={busy} onClick={() => onDescribe(true)} aria-label={`Show ${file.name} on Open Topics as the full description`}>
              Show on Open Topics
            </button>
          ))}
        <button type="button" className="btn small ghost" onClick={onOpen} aria-label={`Open ${file.name} in the preview`}>
          Open
        </button>
        {/* A new tab, not `download`: the server saves the file (attachment) and the browser closes the tab;
            a refusal shows its short page there, not a failed download. This page stays (an upload running is not asked about). */}
        <a
          className="btn small ghost"
          href={links.file(file.id, true)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onFollow}
          aria-label={`Download ${file.name}`}
        >
          Download
        </a>
        {file.canDelete && onDelete && (
          <button type="button" className="btn small danger" disabled={busy} onClick={onDelete} aria-label={`Delete ${file.name}`}>
            {busy ? '…' : 'Delete'}
          </button>
        )}
      </span>
    </li>
  );
}

/** A file on its way: its progress, or why it failed (Retry, ×). No × while it is being saved: too late to cancel. */
export function UploadRow({ u, queue }: { u: Upload; queue: Uploads }) {
  const pct = u.state === 'saving' ? 100 : u.size ? Math.min(100, Math.round((u.loaded / u.size) * 100)) : u.state === 'uploading' ? 0 : 100;
  const meta = u.state === 'queued' ? `waiting · ${fmtSize(u.size)}` : u.state === 'saving' ? `saving · ${fmtSize(u.size)}` : `${pct}% of ${fmtSize(u.size)}`;
  return (
    <li className={`fm-file fm-up ${u.state}`}>
      <FileBadge name={u.name} />
      <span className="fm-main">
        <span className="fm-name" title={u.name}>
          {u.name}
        </span>
        {u.state === 'failed' ? (
          <span className="fm-meta error" role="alert">
            {u.error}
          </span>
        ) : (
          <>
            <span className="fm-bar" role="progressbar" aria-label={`Uploading ${u.name}`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}>
              <span style={{ width: `${pct}%` }} />
            </span>
            <span className="fm-meta">{meta}</span>
          </>
        )}
      </span>
      <span className="fm-acts">
        {u.state === 'failed' && u.retry && (
          <button type="button" className="btn small ghost" onClick={() => queue.retry(u.id)}>
            Retry
          </button>
        )}
        {u.state !== 'saving' && (
          <button
            type="button"
            className="fm-cancel"
            onClick={() => (u.state === 'failed' ? queue.dismiss(u.id) : queue.cancel(u.id))}
            aria-label={u.state === 'failed' ? `Dismiss ${u.name}` : `Cancel the upload of ${u.name}`}
            title={u.state === 'failed' ? 'Dismiss' : 'Cancel'}
          >
            ×
          </button>
        )}
      </span>
    </li>
  );
}
